import { DateTime } from 'luxon';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import {
  AttributeFragmentFragmentGQLType,
  CreateOrUpdateLatestAttributeValueInputGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';

export const attributeValuePropertyLookup = {
  string: 'stringValue',
  float: 'floatValue',
  integer: 'intValue',
  boolean: 'boolValue',
  percentageRate: 'percentageRateValue',
  fullDateStr: 'fullDateStrValue',
  stringArray: 'stringArrayValue',
} as const;

// Updated function using the lookup
export function mapAttributeToGQLValue(
  attribute: Attribute,
): Omit<
  CreateOrUpdateLatestAttributeValueInputGQLType,
  'attributeId' | 'entityDomain' | 'entityIdentifier'
> {
  const propertyName = attributeValuePropertyLookup[attribute.type];

  return {
    type: attribute.type,
    [propertyName]: attribute.value,
  };
}

export const mapSingleAttributeValueToAttribute = (
  attr: AttributeFragmentFragmentGQLType,
  attributeValue: AttributeFragmentFragmentGQLType['attributeValues'][0],
): Attribute => {
  const partial = {
    displayName: attr.displayName,
    attributeId: attr.attributeId,
    effectiveFromReportingDate: attributeValue.effectiveFromReportingDate,
    attributeValueId: attributeValue.attributeValueId,
  };

  switch (attributeValue.__typename) {
    case 'StringAttributeValue':
      return {
        ...partial,
        type: 'string',
        value: attributeValue.stringValue,
        validation: {
          allowedValues: attr.validation.allowedValues,
        },
      };
    case 'FloatAttributeValue':
      return {
        ...partial,
        type: 'float',
        value: attributeValue.floatValue,
        validation: {
          display: attr.validation.display ?? undefined,
        },
      };
    case 'IntegerAttributeValue':
      return {
        ...partial,
        type: 'integer',
        value: attributeValue.integerValue,
        validation: {},
      };
    case 'BooleanAttributeValue':
      return {
        ...partial,
        type: 'boolean',
        value: attributeValue.booleanValue,
        validation: {},
      };
    case 'PercentageRateAttributeValue':
      return {
        ...partial,
        type: 'percentageRate',
        value: attributeValue.percentageRateValue,
        validation: {},
      };
    case 'FullDateStrAttributeValue':
      return {
        ...partial,
        type: 'fullDateStr',
        value: attributeValue.fullDateStrValue,
        validation: {},
      };
    case 'StringArrayAttributeValue':
      return {
        ...partial,
        type: 'stringArray',
        value: attributeValue.stringArrayValue,
        validation: {
          allowedValues: attr.validation.allowedValues,
        },
      };
    default:
      return finleyAssertUnreachable(attributeValue);
  }
};

export const mapEmptyAttributeValueToAttribute = (
  attr: AttributeFragmentFragmentGQLType,
): Attribute => {
  const partial = {
    displayName: attr.displayName,
    attributeId: attr.attributeId,
    value: null,
    effectiveFromReportingDate: null,
    attributeValueId: null,
  };
  // Handle null case with proper typing based on attr.type
  switch (attr.type) {
    case 'string':
      return {
        ...partial,
        type: 'string',
        validation: {
          allowedValues: attr.validation.allowedValues,
        },
      };
    case 'float':
      return {
        ...partial,
        type: 'float',
        validation: {
          display: attr.validation.display ?? undefined,
        },
      };
    case 'integer':
      return {
        ...partial,
        type: 'integer',
        validation: {},
      };
    case 'boolean':
      return {
        ...partial,
        type: 'boolean',
        validation: {},
      };
    case 'percentageRate':
      return {
        ...partial,
        type: 'percentageRate',
        validation: {},
      };
    case 'fullDateStr':
      return {
        ...partial,
        type: 'fullDateStr',
        validation: {},
      };
    case 'stringArray':
      return {
        ...partial,
        type: 'stringArray',
        validation: {
          allowedValues: attr.validation.allowedValues,
        },
      };
    default:
      return finleyAssertUnreachable(attr.type);
  }
};

export const mapGqlAttributeToAttribute = (
  attr: AttributeFragmentFragmentGQLType,
): Attribute => {
  // @makarand-cpv: attributeValue is deprecated.
  if (!attr.attributeValue) {
    return mapEmptyAttributeValueToAttribute(attr);
  }
  return mapSingleAttributeValueToAttribute(attr, attr.attributeValue);
};

// each gql attribute can have multiple attribute values (with different effectiveDates)
// this function will expand those into multiple Attribute objects
export const mapGqlAttributeToAttributeInstances = (
  attr: AttributeFragmentFragmentGQLType,
): Attribute[] => {
  const results: Attribute[] = [];
  attr.attributeValues.forEach((attributeValue) =>
    results.push(mapSingleAttributeValueToAttribute(attr, attributeValue)),
  );

  return results;
};

// This will render the attribute value as a human-readable string
export const renderValue = (attribute: Attribute) => {
  switch (attribute.type) {
    case 'percentageRate': {
      if (attribute.value === null || attribute.value === undefined) return '';
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 20, // Maximum allowed value, if we want to limit this we should do it through maskito
      }).format(attribute.value);
    }
    case 'float': {
      if (attribute.value === null || attribute.value === undefined) return '';

      if (attribute.validation.display?.displayType === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: attribute.validation.display.currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(attribute.value);
      }

      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
      }).format(attribute.value);
    }
    case 'integer': {
      if (attribute.value === null || attribute.value === undefined) return '';
      return new Intl.NumberFormat('en-US').format(attribute.value);
    }
    case 'fullDateStr': {
      const date = DateTime.fromFormat(attribute.value ?? '', 'yyyy-MM-dd');
      return date.isValid ? date.toLocaleString(DateTime.DATE_FULL) : '';
    }
    case 'boolean':
      if (attribute.value === null || attribute.value === undefined)
        return 'N/A';
      return attribute.value === true ? 'Yes' : 'No';
    case 'stringArray': {
      const value = attribute.value ?? [];
      return value.join('\n');
    }
    case 'string':
    default:
      return String(attribute.value ?? '');
  }
};

export const getDefaultFormValue = (attribute: Attribute) => {
  if (attribute.value === null) {
    if (attribute.type === 'stringArray') return [];
    if (attribute.type === 'string') return '';
    return null; // Keep null for other types
  }

  switch (attribute.type) {
    case 'boolean':
      return String(attribute.value);
    default:
      return attribute.value;
  }
};

export const processFormValue = (
  value: string | number | string[] | boolean | null,
  type: Attribute['type'],
) => {
  // Handle empty values - convert to null
  if (value === '' || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  switch (type) {
    case 'boolean':
      return value === 'true';
    default:
      return value;
  }
};

export const getMultiSelectOptions = (
  allowedValues: string[] | null | undefined,
) => {
  if (allowedValues === undefined || allowedValues === null) return [];

  return allowedValues.map((item) => ({ label: item, value: item }));
};
